import { useEffect, useState } from 'react';
import { fhirClient } from 'src/App';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';
import { Bundle } from 'src/careflow/ph-core/fhir-client/resources';

interface RevisitTaskValidationResult {
  isValid: boolean;
  message: string;
  latestTaskStatus?: string;
  latestTask?: Task;
  isLoading: boolean;
}

export const useRevisitTaskValidation = (patientId: string | null, checkListData: any): RevisitTaskValidationResult => {
  const [validationResult, setValidationResult] = useState<RevisitTaskValidationResult>({
    isValid: false,
    message: 'Loading...',
    isLoading: true,
  });

  useEffect(() => {
    if (!patientId) {
      setValidationResult({
        isValid: false,
        message: 'No patient ID provided',
        isLoading: false,
      });
      return;
    }

    if (!checkListData) {
      setValidationResult({
        isValid: false,
        message: 'No checkListData provided',
        isLoading: false,
      });
      return;
    }

    if (!checkListData?.metadata) {
      setValidationResult({
        isValid: false,
        message: 'No checkListData metadata provided',
        isLoading: false,
      });
      return;
    }

    const validateRevisitTask = async () => {
      try {

        const metadata = JSON.parse(checkListData?.metadata);
        const activityDefinitionRef = metadata?.ui?.data?.activityDefinitionRef;
        const revisitActivityDefinitionId = activityDefinitionRef ? activityDefinitionRef.split('/')?.[1] : null;

        if (!revisitActivityDefinitionId || typeof revisitActivityDefinitionId !== 'string') {
          setValidationResult({
            isValid: false,
            message: 'No activityDefinitionId found in checklist metadata',
            isLoading: false,
          });
          return;
        }

        setValidationResult(prev => ({ ...prev, isLoading: true }));
        const response = await fhirClient.search<Task>(
          `Task?subject=${patientId}&code=assessment&_sort=-authored&_count=10&_include=Task:instantiates-canonical`
        );

        const allTasks = response?.entry?.map((entry) => entry?.resource)?.filter((resource): resource is Task => resource?.resourceType === 'Task') || [];
        
        if (allTasks.length === 0) {
          setValidationResult({
            isValid: false, 
            message: 'No assessment tasks found - step executable',
            isLoading: false,
          });
          return;
        }

        let revisitTasks = allTasks.filter((task: Task) => {
          if (!task) return false;
          const instantiatesCanonical = task?.instantiatesCanonical;
          if (!instantiatesCanonical) return false;
          
          const activityDefRef = Array.isArray(instantiatesCanonical) ? instantiatesCanonical[0] : instantiatesCanonical;

          const activityDefId = activityDefRef.split('/')?.[1];

          return activityDefId === revisitActivityDefinitionId;

        });

        console.log('DEBUG useRevisitTaskValidation revisitTasks after initial filter', revisitTasks);

        if (revisitTasks.length === 0) {
          setValidationResult({
            isValid: false,
            message: 'No revisit tasks found - step executable',
            isLoading: false,
          });
          return;
        }

        // Sort revisit tasks by authoredOn date (newest first) to get the latest revisit task
        const sortedRevisitTasks = revisitTasks.sort((a, b) => {
          const dateA = new Date(a?.authoredOn || a?.meta?.lastUpdated || '1970-01-01').getTime();
          const dateB = new Date(b?.authoredOn || b?.meta?.lastUpdated || '1970-01-01').getTime();
          return dateB - dateA; // Newest first
        });

        const latestTask = sortedRevisitTasks[0];
        if (!latestTask) {
          setValidationResult({
            isValid: false,
            message: 'No valid revisit tasks found - step executable',
            isLoading: false,
          });
          return;
        }
        
        const taskStatus = latestTask.status;
        const isStepExecutable = taskStatus !== 'completed';

        let message: string;
        if (taskStatus === 'completed') {
          message = 'All questions answered - step not executable';
        } else if (taskStatus === 'in-progress') {
          message = 'Revisit in progress - step executable';
        } else {
          message = `Revisit task ${taskStatus} - step executable`;
        }

        console.log('Revisit validation result:', {
          latestTaskId: latestTask.id,
          taskStatus,
          isStepExecutable,
          isValid: !isStepExecutable,
          message,
          totalRevisitTasks: sortedRevisitTasks.length
        });

        setValidationResult({
          isValid: !isStepExecutable, 
          message,
          latestTaskStatus: taskStatus,
          latestTask,
          isLoading: false,
        });

      } catch (error) {
        console.error('Error validating revisit task:', error);
        setValidationResult({
          isValid: false,
          message: 'Unable to check revisit task status - allowing execution',
          isLoading: false,
        });
      }
    };

    validateRevisitTask();
  }, [patientId, checkListData]);

  return validationResult;
};

export default useRevisitTaskValidation; 